import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Chip,
  Paper,
  Stack,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import useUserSettings from '../hooks/useUserSettings';
import useHousehold from '../hooks/useHousehold';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../lib/authFetch';
import { NotificationPreferences } from '../types/Notification';
import { HouseholdMember } from '../types/Household';
import { isDevBuild } from '../utils/env';

type FormattingForm = {
  timezone: string;
  locale: string;
  currency: string;
};

const EMPTY_FORMATTING: FormattingForm = {
  timezone: '',
  locale: '',
  currency: '',
};

const getFormattingFromSettings = (formatting?: Partial<FormattingForm> | null): FormattingForm => ({
  timezone: formatting?.timezone ?? '',
  locale: formatting?.locale ?? '',
  currency: formatting?.currency ?? '',
});

const isSameFormatting = (left: FormattingForm | null, right: FormattingForm) =>
  !!left && left.timezone === right.timezone && left.locale === right.locale && left.currency === right.currency;

const COMMON_US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const FALLBACK_TIMEZONES = [
  ...COMMON_US_TIMEZONES,
  'America/Indiana/Indianapolis',
  'America/Boise',
  'America/Detroit',
  'America/Juneau',
  'America/Nome',
  'America/Sitka',
  'America/Adak',
  'UTC',
];

const getLocalDateStamp = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFilenameFromDisposition = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].trim());

  const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (basicMatch?.[1]) return basicMatch[1].trim();

  return null;
};

const getSupportedIntlTimezones = (): string[] => {
  if (typeof Intl === 'undefined') return [];

  const maybeIntl = Intl as unknown as {
    supportedValuesOf?: (key: string) => unknown;
  };
  if (typeof maybeIntl.supportedValuesOf !== 'function') return [];

  try {
    const values = maybeIntl.supportedValuesOf('timeZone');
    if (!Array.isArray(values)) return [];
    return values.filter((value): value is string => typeof value === 'string');
  } catch {
    return [];
  }
};

const getRoleLabel = (role: HouseholdMember['roleInHousehold']): string => {
  if (role === 'primary') return 'Primary';
  if (role === 'partner') return 'Partner';
  return 'Child';
};

const getMemberDisplayName = (member: HouseholdMember): string => {
  const fullName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
  return fullName || member.email;
};

const SettingsPage: React.FC = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const {
    settings,
    loading,
    error,
    save,
    saving,
    saveError,
    reload,
    lastUrl,
    lastStatus,
    lastRequestId,
    lastLedgerMode,
    lastErrorMessage,
    lastUpdatedAt,
  } = useUserSettings();
  const {
    household,
    members,
    loading: householdLoading,
    error: householdError,
    isEmpty: householdEmpty,
    reload: reloadHousehold,
  } = useHousehold();
  const isDev = isDevBuild();
  const canEdit = ['super admin', 'primary', 'partner'].includes(auth.user?.role ?? '');
  const [baseline, setBaseline] = useState<FormattingForm | null>(null);
  const [form, setForm] = useState<FormattingForm>(EMPTY_FORMATTING);
  const [validationError, setValidationError] = useState<{
    timezone?: string;
    locale?: string;
    currency?: string;
  }>({});
  const [saveSuccess, setSaveSuccess] = useState('');
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsError, setPrefsError] = useState('');
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [exporting, setExporting] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState<string>('');

  const availableTimezones = useMemo(() => {
    const intlTimezones = getSupportedIntlTimezones();
    const merged = intlTimezones.length > 0 ? [...COMMON_US_TIMEZONES, ...intlTimezones] : FALLBACK_TIMEZONES;
    return Array.from(new Set(merged));
  }, []);

  useEffect(() => {
    if (!settings?.formatting) return;
    const next = getFormattingFromSettings(settings.formatting);
    setBaseline((current) => (isSameFormatting(current, next) ? current : next));
    setForm((current) => (isSameFormatting(current, next) ? current : next));
  }, [settings]);

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!auth.token) {
        setPreferences(null);
        return;
      }

      setPrefsLoading(true);
      setPrefsError('');
      try {
        const res = await authFetch('/api/notifications/preferences', {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!res.ok) {
          throw new Error('Unable to load notification timezone.');
        }
        const data = (await res.json()) as NotificationPreferences;
        setPreferences(data);
      } catch (_err) {
        setPrefsError('Notification quiet hours timezone is configured in Notifications.');
      } finally {
        setPrefsLoading(false);
      }
    };

    fetchPreferences();
  }, [auth.token]);

  const normalizedCurrency = form.currency.trim().toUpperCase();
  const browserTimezone = useMemo(() => {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return '';
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return '';
    }
  }, []);
  const isDirty = Boolean(
    baseline &&
      (form.timezone !== baseline.timezone || form.locale !== baseline.locale || form.currency !== baseline.currency)
  );
  const canSave = !loading && !saving && !!baseline && isDirty;
  const interactionsDisabled = loading || saving;
  const hasValidationError = Boolean(validationError.timezone || validationError.locale || validationError.currency);

  const quietHoursStatus = useMemo(() => {
    if (!preferences?.quietHours?.enabled) return 'Off';
    const start = preferences.quietHours.start ?? '—';
    const end = preferences.quietHours.end ?? '—';
    const timezone = preferences.quietHours.timezone ?? form.timezone ?? '—';
    return `${start}-${end} (${timezone})`;
  }, [form.timezone, preferences?.quietHours]);

  const notificationsEnabledStatus = useMemo(() => {
    if (!preferences) return '—';
    return preferences.enabled ? 'On' : 'Off';
  }, [preferences]);

  const diagnosticsText = useMemo(
    () =>
      [
        `Last URL: ${lastUrl || '—'}`,
        `Status: ${lastStatus ?? '—'}`,
        `X-Request-Id: ${lastRequestId || '—'}`,
        `X-Ledger-Mode: ${lastLedgerMode || '—'}`,
        `Error text: ${lastErrorMessage || '—'}`,
        `Last updated: ${lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString() : '—'}`,
      ].join('\n'),
    [lastErrorMessage, lastLedgerMode, lastRequestId, lastStatus, lastUpdatedAt, lastUrl]
  );

  const validate = () => {
    const nextError: { timezone?: string; locale?: string; currency?: string } = {};
    if (!form.timezone.trim()) nextError.timezone = 'Timezone is required.';
    if (!form.locale.trim()) nextError.locale = 'Locale is required.';
    if (!/^[A-Za-z]{3}$/.test(normalizedCurrency)) nextError.currency = 'Currency must be 3 letters.';
    setValidationError(nextError);
    return Object.keys(nextError).length === 0;
  };

  const handleCancel = () => {
    if (baseline) {
      setForm(baseline);
    }
    setValidationError({});
    setSaveSuccess('');
  };

  const handleSave = async () => {
    setSaveSuccess('');
    if (!validate()) return;

    const saved = await save({
      formatting: {
        timezone: form.timezone.trim(),
        locale: form.locale.trim(),
        currency: normalizedCurrency,
      },
    });

    if (saved) {
      const next = getFormattingFromSettings(saved.formatting);
      setBaseline(next);
      setForm(next);
      setSaveSuccess('Saved');
      setLastSavedAt(new Date().toISOString());
      setValidationError({});
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const headers: Record<string, string> = {};
      if (auth.token) headers.Authorization = `Bearer ${auth.token}`;

      const res = await authFetch('/api/ledger/export', { headers });
      if (!res.ok) {
        throw new Error('Export failed');
      }

      const blob = await res.blob();
      const filename =
        getFilenameFromDisposition(res.headers.get('Content-Disposition')) ||
        `ledger-export-${getLocalDateStamp()}.csv`;

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (exportError) {
      console.error('Export error:', exportError);
    } finally {
      setExporting(false);
    }
  };

  const handleCopyDebugInfo = async () => {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(diagnosticsText);
      setCopyStatus('Debug info copied.');
    } catch {
      setCopyStatus('Unable to copy debug info.');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Settings
      </Typography>

      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Timezone
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Used for due dates, "today" calculations, and notification timing.
          </Typography>
          {loading ? (
            <Typography color="text.secondary" sx={{ mb: 2 }} data-testid="settings-loading-placeholder">
              Loading your settings...
            </Typography>
          ) : null}
          {error ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Stack spacing={0.25}>
                <Typography color="error">We couldn&apos;t load your settings. Retry, or check you&apos;re logged in.</Typography>
                <Typography variant="caption" color="text.secondary">
                  Details: {error}
                </Typography>
              </Stack>
              <Button variant="outlined" size="small" onClick={reload} disabled={loading}>
                Retry
              </Button>
            </Stack>
          ) : null}
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <Autocomplete
              options={availableTimezones}
              value={form.timezone || null}
              onChange={(_, nextValue) => {
                setForm((current) => ({ ...current, timezone: nextValue ?? '' }));
                setSaveSuccess('');
              }}
              onInputChange={(_, value) => {
                setForm((current) => ({ ...current, timezone: value }));
                setSaveSuccess('');
              }}
              freeSolo
              disabled={!canEdit || interactionsDisabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Timezone"
                  error={Boolean(validationError.timezone)}
                  helperText={validationError.timezone || 'Search timezones (common US zones first).'}
                />
              )}
            />
            {canEdit ? (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="text"
                  onClick={() => {
                    setForm((current) => ({ ...current, timezone: browserTimezone || 'UTC' }));
                    setSaveSuccess('');
                  }}
                  disabled={!browserTimezone || interactionsDisabled}
                >
                  Reset to browser timezone
                </Button>
                <Button variant="outlined" onClick={handleCancel} disabled={!isDirty || saving}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave || hasValidationError}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            ) : null}
            {!browserTimezone ? (
              <Typography variant="caption" color="text.secondary">
                Browser timezone is unavailable on this device.
              </Typography>
            ) : null}
            {saveError ? <Typography color="error">{saveError}</Typography> : null}
            {saveSuccess ? (
              <Typography variant="caption" color="success.main" data-testid="settings-save-success">
                {saveSuccess}
              </Typography>
            ) : null}
            {lastSavedAt ? (
              <Typography variant="caption" color="text.secondary" data-testid="settings-last-saved">
                Last saved: {new Date(lastSavedAt).toLocaleString()}
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Notifications
          </Typography>
          {prefsLoading ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Loading notification preferences...
            </Typography>
          ) : prefsError ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {prefsError}
            </Typography>
          ) : (
            <Stack spacing={0.75} sx={{ mb: 2 }}>
              <Typography>Enabled: {notificationsEnabledStatus}</Typography>
              <Typography>Quiet hours: {quietHoursStatus}</Typography>
            </Stack>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate('/notifications')}
            sx={{ width: 'fit-content' }}
            disabled={loading}
          >
            Manage notification preferences
          </Button>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Household
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Household-shared accounts are visible to everyone in your household. Private accounts are only visible to
            the account owner. Sharing is controlled per account.
          </Typography>

          {householdLoading ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Loading household...
            </Typography>
          ) : null}

          {householdEmpty ? (
            <Stack spacing={0.5}>
              <Typography sx={{ fontWeight: 500 }}>No household yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Household sharing details will appear here once a household is available.
              </Typography>
            </Stack>
          ) : null}

          {householdError ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack spacing={0.25}>
                <Typography color="error">We couldn&apos;t load your household.</Typography>
                <Typography variant="caption" color="text.secondary">
                  Details: {householdError}
                </Typography>
              </Stack>
              <Button variant="outlined" size="small" onClick={reloadHousehold} disabled={householdLoading}>
                Retry
              </Button>
            </Stack>
          ) : null}

          {!householdLoading && !householdEmpty && !householdError ? (
            <Stack spacing={1.25}>
              {household?.name ? (
                <Typography variant="body2" color="text.secondary">
                  Household: {household.name}
                </Typography>
              ) : null}
              {members.map((member) => (
                <Paper key={member.userId} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack spacing={0.2}>
                      <Typography sx={{ fontWeight: 500 }}>{getMemberDisplayName(member)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.email}
                      </Typography>
                    </Stack>
                    <Chip size="small" label={getRoleLabel(member.roleInHousehold)} />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : null}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Data tools
          </Typography>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Export CSV
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Download your transactions as a CSV file for backup or analysis.
              </Typography>
              <Button variant="outlined" onClick={handleExportCsv} disabled={exporting || loading}>
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Import CSV
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Upload a CSV file to add many transactions at once.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/transactions/bulk-upload')} disabled={loading}>
                Import CSV
              </Button>
            </Paper>
          </Stack>
        </Paper>

        <Accordion disabled={loading}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Advanced
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2} sx={{ maxWidth: 420 }}>
              <TextField
                label="Locale"
                value={form.locale}
                onChange={(event) => {
                  setForm((current) => ({ ...current, locale: event.target.value }));
                  setSaveSuccess('');
                }}
                error={Boolean(validationError.locale)}
                helperText={validationError.locale || 'Locale (e.g., en-US)'}
                disabled={!canEdit || interactionsDisabled}
              />
              <TextField
                label="Currency"
                value={form.currency}
                onChange={(event) => {
                  setForm((current) => ({ ...current, currency: event.target.value }));
                  setSaveSuccess('');
                }}
                onBlur={() => setForm((current) => ({ ...current, currency: current.currency.trim().toUpperCase() }))}
                error={Boolean(validationError.currency)}
                helperText={validationError.currency || 'Currency code (e.g., USD)'}
                disabled={!canEdit || interactionsDisabled}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {isDev ? (
          <Accordion disabled={loading}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Diagnostics (dev only)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Stack spacing={0.5}>
                  {diagnosticsText.split('\n').map((line) => (
                    <Typography key={line}>{line}</Typography>
                  ))}
                </Stack>
                <Button variant="outlined" onClick={handleCopyDebugInfo} sx={{ width: 'fit-content' }}>
                  Copy debug info
                </Button>
                {copyStatus ? <Alert severity="info">{copyStatus}</Alert> : null}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Stack>
    </Box>
  );
};

export default SettingsPage;
